/**
 * Parser for Import Note Feature
 * Converts validated text into flashcard import pairs.
 */
import { splitReadingStoryLines } from '../../../utils/readingStory';

class ImportParser {
  isBlockSeparator(line) {
    const trimmed = String(line || '').trim();
    if (/^-{3,}/.test(trimmed)) {
      return true;
    }
    if (/^[?-]+$/.test(trimmed) && trimmed.includes('-') && trimmed.length >= 2) {
      return true;
    }
    return /^[\p{Dash_Punctuation}-]+$/u.test(trimmed) &&
      (trimmed.length >= 3 || (trimmed.length >= 2 && /[\p{Dash_Punctuation}]/u.test(trimmed)));
  }

  getContentLines(text) {
    return String(text || '').split('\n').filter((line) => line.trim() !== '');
  }

  isTaggedFormat(text) {
    return String(text || '')
      .split('\n')
      .some((line) => /^(@q:|@a:|q:|a:|r:|d:|@d:|@r:|@pre:|@front:|@after:|@back:|@word:|@tts:)/i.test(line.trim()));
  }

  isTaggedBlock(block) {
    return (block || []).some((line) =>
      /^(@q:|@a:|q:|a:|r:|d:|@d:|@r:|@pre:|@front:|@after:|@back:|@word:|@tts:)/i.test(String(line || '').trim())
    );
  }

  splitBlocks(text) {
    const blocks = [];
    let currentBlock = [];

    String(text || '').replace(/\r\n/g, '\n').split('\n').forEach((line) => {
      if (this.isBlockSeparator(line)) {
        if (currentBlock.length > 0) {
          blocks.push(currentBlock);
          currentBlock = [];
        }
        return;
      }
      currentBlock.push(line);
    });

    if (currentBlock.length > 0) {
      blocks.push(currentBlock);
    }

    return blocks.filter((block) => block.some((line) => line.trim() !== ''));
  }

  splitReadingSentences(text) {
    return splitReadingStoryLines(text);
  }

  applyReadingPair(pair, value) {
    const readingText = String(value || '').trim();
    const sentences = this.splitReadingSentences(readingText);

    pair.cardType = 'reading';
    pair.readingText = readingText;
    pair.readingTitle = sentences[0] || '';
    pair.readingContent = sentences.slice(1).join('\n');
    pair.question = pair.readingTitle;
    pair.answer = pair.readingContent;
  }

  parseReadingTagLine(line) {
    const match = String(line || '').trim().match(/^@([AB]\s+name|[AB](?:\[once\])?|R|Scene|Story)\s*:\s*(.*)$/i);
    if (!match) return null;
    return {
      tag: match[1].replace(/\[once\]/i, '').replace(/\s+/g, ' ').toLowerCase(),
      ...(/\[once\]/i.test(match[1]) ? { practiceMode: 'once' } : {}),
      value: match[2].trim(),
    };
  }

  buildReadingSegments(title, lines = []) {
    const readingTitle = String(title || '').trim();
    const readingCharacterNames = { A: 'A', B: 'B' };
    const segments = [];
    let activeSegment = null;

    lines.forEach((line) => {
      const trimmed = String(line || '').trim();
      if (!trimmed) return;

      const tagged = this.parseReadingTagLine(trimmed);
      if (tagged) {
        if (tagged.tag === 'a name' || tagged.tag === 'b name') {
          const speaker = tagged.tag[0].toUpperCase();
          if (tagged.value) readingCharacterNames[speaker] = tagged.value;
          activeSegment = null;
          return;
        }

        if (tagged.tag === 'scene') {
          activeSegment = {
            type: 'scene',
            text: tagged.value,
            practice: false,
          };
          segments.push(activeSegment);
          return;
        }

        if (tagged.tag === 'story') {
          activeSegment = {
            type: 'story',
            text: tagged.value,
            practice: true,
          };
          segments.push(activeSegment);
          return;
        }

        if (tagged.tag === 'a' || tagged.tag === 'b') {
          activeSegment = {
            type: 'speech',
            speaker: tagged.tag.toUpperCase(),
            ...(tagged.practiceMode ? { practiceMode: tagged.practiceMode } : {}),
            text: tagged.value,
            practice: true,
          };
          segments.push(activeSegment);
        }
        return;
      }

      if (activeSegment) {
        activeSegment.text = [activeSegment.text, trimmed].filter(Boolean).join('\n');
      }
    });

    const normalizedSegments = segments.filter((segment) => String(segment.text || '').trim());
    const readingSentences = normalizedSegments.flatMap((segment) => {
      if (segment.type === 'story') return this.splitReadingSentences(segment.text);
      if (segment.type === 'speech' && segment.text) return [segment.text];
      return [];
    });
    const readingContent = normalizedSegments
      .map((segment) => {
        if (segment.type === 'scene') return `@Scene: ${segment.text}`;
        if (segment.type === 'story') return `@Story: ${segment.text}`;
        return `@${segment.speaker}${segment.practiceMode === 'once' ? '[once]' : ''}: ${segment.text}`;
      })
      .join('\n');

    return {
      readingTitle,
      readingContent,
      readingText: [`@R: ${readingTitle}`, readingContent].filter(Boolean).join('\n'),
      readingSegments: normalizedSegments,
      readingSentences,
      readingCharacterNames,
    };
  }

  applyReadingSegmentsPair(pair, title, lines) {
    const readingData = this.buildReadingSegments(title, lines);
    pair.cardType = 'reading';
    pair.readingText = readingData.readingText;
    pair.readingTitle = readingData.readingTitle;
    pair.readingContent = readingData.readingContent;
    pair.readingSentences = readingData.readingSentences;
    pair.readingSegments = readingData.readingSegments;
    pair.readingCharacterNames = readingData.readingCharacterNames;
    pair.question = readingData.readingTitle;
    pair.answer = readingData.readingContent;
  }

  parseTagged(text) {
    return this.splitBlocks(text).map((block) => {
      const pair = {
        question: '',
        answer: '',
        cardType: undefined,
        readingText: undefined,
        readingTitle: undefined,
        readingContent: undefined,
        readingSentences: undefined,
        readingSegments: undefined,
        readingCharacterNames: undefined,
        contextPre: null,
        memo: null,
        contextAfter: null,
        backMemo: null,
        word: undefined,
      };

      for (let index = 0; index < block.length; index += 1) {
        const line = block[index];
        const trimmed = line.trim();
        const separatorIndex = trimmed.indexOf(':');
        if (separatorIndex < 0) continue;

        const tag = trimmed.slice(0, separatorIndex).trim().toLowerCase();
        const value = trimmed.slice(separatorIndex + 1).trim();

        if (tag === '@q') pair.question = value;
        if (tag === '@a') pair.answer = value;
        if (tag === 'q' || tag === 'a') {
          throw new Error(`Legacy ${tag.toUpperCase()}: flashcard import is no longer supported. Use @${tag.toUpperCase()}: instead.`);
        }
        if (tag === 'r' || tag === 'd' || tag === '@d') {
          throw new Error(`Legacy ${tag.toUpperCase()}: reading/dialog import is no longer supported. Use @R:.`);
        }
        if (tag === '@r') {
          this.applyReadingSegmentsPair(pair, value, block.slice(index + 1));
          break;
        }
        if (tag === '@pre') pair.contextPre = value || null;
        if (tag === '@front') pair.memo = value || null;
        if (tag === '@after') pair.contextAfter = value || null;
        if (tag === '@back') pair.backMemo = value || null;
        if (tag === '@tts') pair.ttsText = value || null;
        if (tag === '@word') pair.word = /^(true|yes|y|1|word|simple)$/i.test(value);
      }

      return pair;
    });
  }

  parsePlain(text) {
    const lines = this.getContentLines(text).filter((line) => !this.isBlockSeparator(line));
    const pairs = [];

    let i = 0;
    while (i < lines.length) {
      const question = lines[i];
      i++;

      let memo = null;
      if (i < lines.length && lines[i].startsWith(';')) {
        memo = lines[i].slice(1).trim();
        i++;
      }

      const answer = lines[i];
      i++;

      pairs.push({ question, answer, memo });
    }

    return pairs;
  }

  parse(text) {
    if (this.isTaggedFormat(text)) {
      return this.splitBlocks(text).flatMap((block) => (
        this.isTaggedBlock(block)
          ? this.parseTagged(block.join('\n'))
          : this.parsePlain(block.join('\n'))
      ));
    }

    return this.parsePlain(text);
  }
}

export default ImportParser;
